import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid, Typography, Box, Paper, Button, Stack } from "@mui/material";
import { BudgetChart, BudgetTable, SummaryCards } from "../components/Budget";
import type { Category } from "../components/Budget";
import { api } from "../services/api";
import { useDebounce } from "../hooks/useDebounce";

export default function BudgetPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [budgetPeriodId, setBudgetPeriodId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<"load" | "save" | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allocationsVersion, setAllocationsVersion] = useState(0);

  const categoriesRef = useRef<Category[]>([]);
  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  const lastSavedVersionRef = useRef(0);
  const lastAttemptedVersionRef = useRef(0);
  const debouncedAllocationsVersion = useDebounce(allocationsVersion, 900);

  const loadBudget = async () => {
    setLoading(true);
    setError(null);
    setErrorKind(null);
    try {
      const resp = await api.getBudget();
      setBudgetPeriodId(resp.period.id);
      setCategories(resp.categories as any);
      setDirty(false);
      setAllocationsVersion(0);
      lastSavedVersionRef.current = 0;
      lastAttemptedVersionRef.current = 0;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load budget');
      setErrorKind("load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBudget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flattenLeafBudgets = (cats: Category[]) => {
    const out: Array<{ categoryId: string; allocatedCents: number }> = [];
    const walk = (node: Category) => {
      const hasSubs = !!node.subcategories?.length;
      if (hasSubs) node.subcategories!.forEach(walk);
      else out.push({ categoryId: node.id, allocatedCents: Math.round((node.budgeted || 0) * 100) });
    };
    cats.forEach(walk);
    return out;
  };

  const saveAllocationsNow = async (reason: "autosave" | "structure" | "retry" = "autosave") => {
    if (!budgetPeriodId) return false;
    const versionAtStart = allocationsVersion;
    setSaving(true);
    setError(null);
    setErrorKind(null);
    try {
      const allocations = flattenLeafBudgets(categoriesRef.current);
      await api.saveBudgetAllocations(budgetPeriodId, allocations);

      lastSavedVersionRef.current = versionAtStart;
      lastAttemptedVersionRef.current = versionAtStart;
      if (versionAtStart === allocationsVersion) {
        setDirty(false);
      }
      return true;
    } catch (e) {
      lastAttemptedVersionRef.current = versionAtStart;
      setError(e instanceof Error ? e.message : 'Failed to save budget');
      setErrorKind("save");
      if (reason === "autosave") {
        // Leave dirty=true so the UI reflects unsaved changes.
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Debounced autosave for budget amount edits
  useEffect(() => {
    if (!budgetPeriodId) return;
    if (loading) return;
    if (!dirty) return;

    const version = debouncedAllocationsVersion;
    if (version === 0) return;
    if (saving) return;
    if (version === lastSavedVersionRef.current) return;
    if (version === lastAttemptedVersionRef.current) return;

    void saveAllocationsNow("autosave");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAllocationsVersion, budgetPeriodId, dirty, loading, saving]);

  // --- Compute totals for income and expenses separately ---
  const totals = useMemo(() => {
    const sumRecursive = (cats: Category[]) =>
      cats.reduce(
        (acc, c) => {
          const walk = (node: Category, parentKind?: "income" | "expense"): void => {
            const inferredKind: "income" | "expense" =
              node.kind ??
              parentKind ??
              (node.name.toLowerCase().includes("income") || node.id.includes("income")
                ? "income"
                : "expense");

            const hasSubs = !!node.subcategories?.length;

            if (hasSubs && node.subcategories) {
              node.subcategories.forEach((sub) => walk(sub, inferredKind));
            }

            if (!hasSubs) {
              const intervalMonths = node.billingIntervalMonths && node.billingIntervalMonths > 0
                ? node.billingIntervalMonths
                : 1;
              const monthlyActual = node.actual / intervalMonths;

              if (inferredKind === "income") {
                acc.incomeBudgeted += node.budgeted;
                acc.incomeActual += monthlyActual;
              } else {
                acc.expensesBudgeted += node.budgeted;
                acc.expensesActual += monthlyActual;
              }
            }
          };

          walk(c, c.kind);

          return acc;
        },
        {
          incomeBudgeted: 0,
          incomeActual: 0,
          expensesBudgeted: 0,
          expensesActual: 0,
        }
      );

    return sumRecursive(categories);
  }, [categories]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        p: { xs: 1, sm: 2, md: 3 },
        pb: { xs: 4, md: 6 },
      }}
    >
      {/* <Typography variant="h4" gutterBottom>
        Monthly Budget
      </Typography> */}

      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          {loading ? (
            <Typography variant="body2" color="text.secondary">Loading budget…</Typography>
          ) : error ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="error">{error}</Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  if (errorKind === "load") void loadBudget();
                  else void saveAllocationsNow("retry");
                }}
                disabled={saving || loading || (errorKind === "save" && !dirty)}
              >
                Retry
              </Button>
            </Stack>
          ) : dirty ? (
            <Typography variant="body2" color="warning.main">
              {saving ? "Saving…" : "Pending autosave…"}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary"></Typography>
            // <Typography variant="body2" color="text.secondary">All changes saved</Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {/* --- Summary Cards --- */}
        <Grid size={{ xs: 12, sm: 12, lg: 6 }}>
          <SummaryCards totals={totals} />
        </Grid>

        {/* --- Budget vs Actual Chart --- */}
        <Grid size={{ xs: 12, sm: 12, lg: 6 }}>
          <Paper elevation={3} sx={{ p: 2, minHeight: 340, height: 340, display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>
              Budgeted vs Actual Cashflow
            </Typography>
            <BudgetChart categories={categories} />
          </Paper>
        </Grid>

        {/* --- Table --- */}
        <Grid size={{ xs: 12, sm: 12, lg: 12 }}>
          <Box sx={{ overflowX: 'auto' }}>
          <BudgetTable
            categories={categories}
            onCreateCategory={async (input) => {
              const resp = await api.createCategory({
                name: input.name,
                parentId: input.parentId,
                kind: input.kind,
                billingIntervalMonths: input.billingIntervalMonths,
                sortOrder: input.sortOrder,
              });

              return {
                id: resp.category.id,
                name: resp.category.name,
                kind: (resp.category.kind ?? input.kind) as any,
                billingIntervalMonths: resp.category.billingIntervalMonths ?? input.billingIntervalMonths,
                sortOrder: resp.category.sortOrder ?? input.sortOrder,
              };
            }}
            onUpdateCategory={async (id, patch) => {
              await api.updateCategory(id, patch);
            }}
            onDeleteCategory={async (id) => {
              await api.deleteCategory(id);
            }}
            onChange={(updated, meta) => {
              setCategories(updated);
              if (meta?.change === 'budget') {
                setDirty(true);
                setAllocationsVersion((v) => v + 1);
              }

              if (meta?.change === 'structure') {
                // Category mutations are persisted immediately via API, but we refresh
                // from the server to keep the tree canonical and in sync.
                void (async () => {
                  const ok = dirty ? await saveAllocationsNow("structure") : true;
                  if (ok) await loadBudget();
                })();
              }
            }}
          />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
